import DOMPurify from "dompurify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface INotificationBannerItem {
  id: string;
  title?: string;
  content: string;
  type?: string;
}

interface IUseNotificationBannerLogicOptions<
  T extends INotificationBannerItem,
> {
  fetchFn: () => Promise<T[]>;
  markSeenFn: (id: string) => Promise<void>;
  onLoaded?: (items: T[]) => void;
  onDismissed?: (id: string, remaining: T[]) => void;
}

export function useNotificationBannerLogic<T extends INotificationBannerItem>({
  fetchFn,
  markSeenFn,
  onLoaded,
  onDismissed,
}: IUseNotificationBannerLogicOptions<T>) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [visibleItems, setVisibleItems] = useState<T[]>([]);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const onDismissedRef = useRef(onDismissed);
  onDismissedRef.current = onDismissed;

  useEffect(() => {
    const run = async () => {
      try {
        const data = await fetchFn();
        setVisibleItems(data);
        onLoadedRef.current?.(data);
      } finally {
        setIsLoading(false);
      }
    };
    void run();
  }, []);

  const handleDismiss = useCallback(
    (id: string): void => {
      void markSeenFn(id).then(() => {
        setVisibleItems((prev) => {
          const remaining = prev.filter((item) => item.id !== id);
          onDismissedRef.current?.(id, remaining);
          return remaining;
        });
      });
    },
    [markSeenFn],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && visibleItems.length > 0) {
        if ((e.target as Element)?.closest('[role="dialog"]')) return;
        const last = visibleItems[visibleItems.length - 1];
        void handleDismiss(last.id);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visibleItems, handleDismiss]);

  const sanitizedContent = useMemo(
    () =>
      Object.fromEntries(
        visibleItems.map((item) => [item.id, DOMPurify.sanitize(item.content)]),
      ),
    [visibleItems],
  );

  return { isLoading, visibleItems, handleDismiss, sanitizedContent };
}
